import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import questConfig from '../config/questConfig';
import supabase from '../lib/supabase';

const { FiDollarSign, FiCalendar, FiUsers, FiStar, FiPlus, FiEdit, FiTrash2, FiEye, FiTrendingUp, FiAward, FiTarget, FiHome, FiSettings, FiBookmark, FiUser } = FiIcons;

const ProDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    price: '',
    category: ''
  });

  // Use authenticated user ID or fallback for demo
  const userId = user?.userId || localStorage.getItem('userId') || 'demo-user';
  const token = user?.token || localStorage.getItem('token') || 'demo-token';

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      // For now, use mock data since Supabase isn't connected
      const mockServices = [
        {
          id: 1,
          name: 'Kitchen Renovation',
          description: 'Complete kitchen renovation services',
          price: 2500,
          category: 'Home Improvement'
        },
        {
          id: 2,
          name: 'Bathroom Repair',
          description: 'Bathroom plumbing and repair services',
          price: 450,
          category: 'Plumbing'
        }
      ];
      
      setServices(mockServices);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const newServiceData = {
        ...newService,
        id: Date.now(), // Temporary ID
        price: parseFloat(newService.price)
      };
      
      setServices([...services, newServiceData]);
      setNewService({ name: '', description: '', price: '', category: '' });
      setShowAddService(false);
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleDeleteService = (serviceId) => {
    setServices(services.filter(service => service.id !== serviceId));
  };

  const stats = [
    { label: 'Total Earnings', value: '$2,450', icon: FiDollarSign, color: 'text-green-600' },
    { label: 'Active Jobs', value: '12', icon: FiCalendar, color: 'text-blue-600' },
    { label: 'Total Clients', value: '48', icon: FiUsers, color: 'text-purple-600' },
    { label: 'Average Rating', value: '4.8', icon: FiStar, color: 'text-yellow-600' }
  ];

  const achievements = [
    { title: 'Top Performer', description: 'Completed 50+ jobs this month', icon: FiTrendingUp, color: 'bg-green-100 text-green-600' },
    { title: 'Customer Favorite', description: 'Maintained 4.8+ rating', icon: FiStar, color: 'bg-yellow-100 text-yellow-600' },
    { title: 'Quick Responder', description: 'Average response time: 2 hours', icon: FiTarget, color: 'bg-blue-100 text-blue-600' },
    { title: 'Verified Professional', description: 'Background checked & licensed', icon: FiAward, color: 'bg-purple-100 text-purple-600' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiHome },
    { id: 'achievements', label: 'Achievements', icon: FiAward },
    { id: 'services', label: 'My Services', icon: FiBookmark },
    { id: 'bookings', label: 'Bookings', icon: FiCalendar },
    { id: 'profile', label: 'Profile', icon: FiUser }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Professional Dashboard
          </h1>
          <p className="text-gray-600">
            Manage your services, bookings, and grow your business
          </p>
          {!isAuthenticated && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Demo Mode:</strong> You're viewing the dashboard in demo mode. 
                <a href="/login" className="text-blue-600 hover:underline ml-1">Sign in</a> to access your personal dashboard.
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center ${stat.color}`}>
                  <SafeIcon icon={stat.icon} className="text-xl" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enhanced Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          {/* Desktop Tabs */}
          <div className="hidden md:block border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="text-lg" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Mobile Tabs */}
          <div className="md:hidden border-b border-gray-200">
            <div className="px-4 py-2">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiHome} className="text-primary-600 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Dashboard Overview
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Recent Activity</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <SafeIcon icon={FiDollarSign} className="text-green-600 text-sm" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Payment Received</p>
                            <p className="text-xs text-gray-500">John Smith - Kitchen Renovation</p>
                          </div>
                        </div>
                        <span className="text-green-600 font-semibold text-sm">+$450</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <SafeIcon icon={FiCalendar} className="text-blue-600 text-sm" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">New Booking</p>
                            <p className="text-xs text-gray-500">Sarah Johnson - Bathroom Repair</p>
                          </div>
                        </div>
                        <span className="text-blue-600 font-semibold text-sm">Tomorrow</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                            <SafeIcon icon={FiStar} className="text-purple-600 text-sm" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">New Review</p>
                            <p className="text-xs text-gray-500">5-star review from Maria Garcia</p>
                          </div>
                        </div>
                        <span className="text-purple-600 font-semibold text-sm">★ 5.0</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Quick Actions</h4>
                    <div className="space-y-3">
                      <button
                        onClick={() => setActiveTab('services')}
                        className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
                      >
                        <SafeIcon icon={FiPlus} className="mr-2" />
                        Add New Service
                      </button>
                      <button
                        onClick={() => setActiveTab('bookings')}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        <SafeIcon icon={FiCalendar} className="mr-2" />
                        View Bookings
                      </button>
                      <button
                        onClick={() => setActiveTab('profile')}
                        className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                      >
                        <SafeIcon icon={FiSettings} className="mr-2" />
                        Update Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'achievements' && (
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiAward} className="text-primary-600 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Your Achievements
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {achievements.map((achievement, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-gray-50 rounded-lg p-6"
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${achievement.color}`}>
                          <SafeIcon icon={achievement.icon} className="text-xl" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {achievement.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {achievement.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
                  <h4 className="text-lg font-semibold mb-2">Professional Growth</h4>
                  <p className="text-primary-100 mb-4">
                    You're performing exceptionally well! Keep up the great work to unlock more achievements.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-primary-100">Progress to next level</span>
                    <span className="text-sm font-semibold">85%</span>
                  </div>
                  <div className="w-full bg-primary-500 rounded-full h-2 mt-2">
                    <div className="bg-white h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiBookmark} className="text-primary-600 text-xl" />
                    <h3 className="text-xl font-semibold text-gray-900">
                      My Services
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowAddService(true)}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="mr-2" />
                    Add Service
                  </button>
                </div>

                {/* Add Service Modal */}
                {showAddService && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h4 className="text-lg font-semibold mb-4">Add New Service</h4>
                      <form onSubmit={handleAddService} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Name
                          </label>
                          <input
                            type="text"
                            value={newService.name}
                            onChange={(e) => setNewService({...newService, name: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={newService.description}
                            onChange={(e) => setNewService({...newService, description: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            rows={3}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Price ($)
                          </label>
                          <input
                            type="number"
                            value={newService.price}
                            onChange={(e) => setNewService({...newService, price: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                          </label>
                          <select
                            value={newService.category}
                            onChange={(e) => setNewService({...newService, category: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                          >
                            <option value="">Select Category</option>
                            <option value="Cleaning">Cleaning</option>
                            <option value="Plumbing">Plumbing</option>
                            <option value="Electrical">Electrical</option>
                            <option value="Landscaping">Landscaping</option>
                            <option value="Home Improvement">Home Improvement</option>
                          </select>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="submit"
                            className="flex-1 bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 transition-colors"
                          >
                            Add Service
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowAddService(false)}
                            className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service) => (
                    <div key={service.id} className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">{service.name}</h4>
                      <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-primary-600 font-semibold">${service.price}</span>
                        <div className="flex space-x-2">
                          <button className="text-gray-400 hover:text-gray-600">
                            <SafeIcon icon={FiEye} />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <SafeIcon icon={FiEdit} />
                          </button>
                          <button 
                            onClick={() => handleDeleteService(service.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'bookings' && (
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiCalendar} className="text-primary-600 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Upcoming Bookings
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Kitchen Renovation Consultation</h4>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Tomorrow</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <p className="font-medium text-gray-900">Client</p>
                        <p>John Smith</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Time</p>
                        <p>2:00 PM - 4:00 PM</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Location</p>
                        <p>123 Main St, New York</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Bathroom Repair</h4>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">This Week</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <p className="font-medium text-gray-900">Client</p>
                        <p>Sarah Johnson</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Time</p>
                        <p>Friday, 10:00 AM</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Location</p>
                        <p>456 Oak Ave, Brooklyn</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiUser} className="text-primary-600 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Profile Settings
                  </h3>
                </div>
                
                <div className="max-w-2xl">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Enter your business name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Describe your services and experience"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Areas
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="New York, Brooklyn, Queens"
                      />
                    </div>
                    <button className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProDashboard;